import { Card, CardBody, Divider, ScrollShadow } from "@heroui/react";
import { LimeInputProps, LimeInput } from "./LimeInput";
import { AnimatePresence, motion } from "motion/react";
import { Fragment } from "react/jsx-runtime";
import {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  type ReactNode,
} from "react";
import { cn } from "@/utils";
import { LimePhoneInput, LimePhoneInputProps } from "./LimePhoneInput";

type SharedProps = {
  items: {
    label: string | ReactNode;
    key: string;
  }[];
  onSelect: (key: string) => void;
  wrapperClassName?: HTMLDivElement["className"];
  selectFirstOnEnter?: boolean;
};

export type LimeSelectPhoneInputProps = SharedProps & {
  inputType: "phone";
  inputProps: LimePhoneInputProps;
};

export type LimeSelectTextInputProps = SharedProps & {
  inputType: "text";
  inputProps: LimeInputProps;
};

export type LimeSelectInputProps =
  | LimeSelectPhoneInputProps
  | LimeSelectTextInputProps;

export const LimeSelectInput = ({
  inputType,
  inputProps,
  items,
  onSelect,
  wrapperClassName,
  selectFirstOnEnter = false,
}: LimeSelectInputProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const [inputHeight, setInputHeight] = useState(40); // Default fallback height
  const [activeIndex, setActiveIndex] = useState(0);

  const valueLen =
    typeof inputProps.value === "string" ? inputProps.value.length : 0;
  const isOpen =
    (isInputFocused || isDropdownFocused) && valueLen > 0 && items.length > 0;

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  useEffect(() => {
    if (isOpen && items.length > 0) {
      setActiveIndex(0);
    }
  }, [isOpen, items.length]);

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const focusItem = (index: number) => {
    if (items.length === 0) {
      return;
    }

    const normalizedIndex =
      ((index % items.length) + items.length) % items.length;
    const element = itemRefs.current[normalizedIndex];

    element?.focus();
  };

  const handleSelect = (key: string) => {
    onSelect(key);
    setIsDropdownFocused(false);
    setIsInputFocused(false);
    focusInput();

    if (document.activeElement === inputRef.current) {
      setIsInputFocused(true);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      selectFirstOnEnter &&
      event.key === "Enter" &&
      isOpen &&
      items.length > 0
    ) {
      event.preventDefault();
      handleSelect(items[0].key);
    }
  };

  const moveFocusToIndex = (index: number) => {
    if (items.length === 0) {
      return;
    }

    const normalizedIndex =
      ((index % items.length) + items.length) % items.length;

    setActiveIndex(normalizedIndex);
    focusItem(normalizedIndex);
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    keyValue: string,
  ) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveFocusToIndex(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocusToIndex(index - 1);
        break;
      case "Home":
        event.preventDefault();
        moveFocusToIndex(0);
        break;
      case "End":
        event.preventDefault();
        moveFocusToIndex(items.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        handleSelect(keyValue);
        break;
      default:
        break;
    }
  };

  useLayoutEffect(() => {
    // Try measuring both the container and the input element
    const containerElement = containerRef.current;

    if (containerElement) {
      const containerHeight = containerElement.offsetHeight;

      if (containerHeight > 0) {
        setInputHeight(containerHeight);
        return;
      }
    }
  }, [inputProps.value, isInputFocused]); // Re-measure when value or focus changes

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(true);
    inputProps.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const nextFocusTarget = e.relatedTarget as Node | null;
    const isMovingIntoDropdown =
      !!nextFocusTarget &&
      dropdownRef.current?.contains(nextFocusTarget) === true;

    setIsInputFocused(false);
    setIsDropdownFocused(isMovingIntoDropdown);
    inputProps.onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleInputKeyDown(e);
    inputProps.onKeyDown?.(e);
  };

  return (
    <div className={cn("relative w-full", wrapperClassName)} ref={containerRef}>
      {inputType === "phone" ? (
        <LimePhoneInput
          {...inputProps}
          ref={inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <LimeInput
          {...inputProps}
          ref={inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDownCapture={() => setIsDropdownFocused(true)}
            onFocusCapture={() => setIsDropdownFocused(true)}
            onBlurCapture={(e: React.FocusEvent<HTMLDivElement>) => {
              const nextFocusTarget = e.relatedTarget as Node | null;
              const isStayingInside =
                !!nextFocusTarget &&
                dropdownRef.current?.contains(nextFocusTarget) === true;

              if (!isStayingInside) {
                setIsDropdownFocused(false);
              }
            }}
            initial={{ opacity: 0, y: -5, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.9 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="rounded-large shadow-medium absolute left-0 z-100000 w-full bg-white px-2 py-2 subpixel-antialiased"
            style={{
              top: inputHeight + 4, // 4px gap between input and dropdown
            }}
            role="listbox"
            data-open="true"
          >
            <ScrollShadow className="max-h-64">
              <div
                className="flex flex-col gap-2 px-1 py-1"
                data-identifier="lime-select-input-dropdown"
              >
                {items.map((item, index) => (
                  <Fragment key={item.key}>
                    <Card
                      data-identifier={`lime-select-input-dropdown-item-${item.key}`}
                      ref={(node: HTMLDivElement | null) => {
                        itemRefs.current[index] = node;
                      }}
                      onPress={() => handleSelect(item.key)}
                      isPressable
                      shadow="none"
                      isHoverable
                      role="option"
                      tabIndex={activeIndex === index ? 0 : -1}
                      aria-selected={activeIndex === index}
                      onFocus={() => setActiveIndex(index)}
                      onKeyDown={(event) =>
                        handleOptionKeyDown(event, index, item.key)
                      }
                    >
                      <CardBody>
                        {typeof item.label === "string" ? (
                          <p>{item.label}</p>
                        ) : (
                          item.label
                        )}
                      </CardBody>
                    </Card>

                    {index !== items.length - 1 && <Divider />}
                  </Fragment>
                ))}
              </div>
            </ScrollShadow>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
